import React, { useState, useEffect } from 'react';
import { FaShoppingCart, FaCog } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { BuyTicketModal } from '../modals';
import { LoginModal, ProfileModal } from '../auth';
import { CartModal } from '../cart';
import UserDropdown from './UserDropdown';
import Logo from './Logo';
import { authService } from '../../services/auth';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { User } from '../../types/auth';
import { useDropdown } from '../../hooks';
import { ticketApi } from '../../services/ticketApi';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [isBuyModalOpen, setBuyModalOpen] = useState(false);
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const [isCartModalOpen, setCartModalOpen] = useState(false);
  const [isProfileModalOpen, setProfileModalOpen] = useState(false);

  const [userInfo, setUserInfo] = useState<User | null>(() => {
    const stored = localStorage.getItem('userInfo');
    return stored ? JSON.parse(stored) : null;
  });
  const { cartItems, updateQuantity, removeFromCart, clearCart } = useCart();
  const { isAuthenticated, setIsAuthenticated } = useAuth();
  const { dropdownOpen, setDropdownOpen, dropdownRef } = useDropdown();
  const { showSuccess, showError } = useNotifications();

  // Sayfa yüklendiğinde/yenilendiğinde kullanıcı bilgilerini kontrol et
  useEffect(() => {
    const checkUserInfo = async () => {
      if (isAuthenticated && userInfo?.id) {
        // Son güncelleme zamanını kontrol et (localStorage'dan)
        const lastUpdate = localStorage.getItem('userInfoLastUpdate');
        const now = Date.now();
        const fifteenMinutes = 15 * 60 * 1000; // 15 dakika in milliseconds

        // Eğer 15 dakikadan fazla güncellenmemişse veya hiç güncellenmemişse, otomatik yenile
        if (!lastUpdate || now - parseInt(lastUpdate) > fifteenMinutes) {
          try {
            const updatedUser = await authService.refreshUserInfo(userInfo.id);
            setUserInfo(updatedUser);
            localStorage.setItem('userInfoLastUpdate', now.toString());
            console.log('✅ Kullanıcı bilgileri otomatik güncellendi');
          } catch (error) {
            // Sessizce fail - kullanıcıyı rahatsız etme
            console.log('❌ Otomatik bilgi güncelleme başarısız:', error);
          }
        }
      }
    };

    // Sayfa ilk yüklendiğinde hemen kontrol et
    checkUserInfo();

    // Sayfa odağa geldiğinde de kontrol et (browser tab değiştirip geri gelince)
    const handleFocus = () => {
      checkUserInfo();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, userInfo?.id, setUserInfo]);

  const handleLoginSuccess = (user: User) => {
    setIsAuthenticated(true);
    setUserInfo(user);
    // Login sırasında güncelleme zamanını kaydet
    localStorage.setItem('userInfoLastUpdate', Date.now().toString());
  };

  const handleLogout = () => {
    authService.clearToken();
    localStorage.removeItem('userInfo');
    setIsAuthenticated(false);
    setUserInfo(null);
    setDropdownOpen(false);
  };

  // Fiş satın alındığında ticket_count'u güncelle
  const handleBuyTicket = (count: number, newBalance: number) => {
    setUserInfo(prev => (prev ? { ...prev, ticket_count: newBalance } : prev));
    // LocalStorage'ı da güncelle
    if (userInfo) {
      const updatedUser = { ...userInfo, ticket_count: newBalance };
      localStorage.setItem('userInfo', JSON.stringify(updatedUser));
    }
  };

  // Sipariş tamamlama işlemi (fiş kullanımı)
  const handleOrderComplete = async (
    totalCost: number,
    departmentData: { management: string; office: string }
  ) => {
    if (!userInfo?.id) {
      throw new Error('Kullanıcı bilgisi bulunamadı');
    }

    try {
      // Sipariş verisini hazırla
      const orderData = {
        user_id: userInfo.id,
        user_name: userInfo.name,
        user_surname: userInfo.surname,
        management: departmentData.management,
        office: departmentData.office,
        total_amount: totalCost,
        ticket_used: totalCost,
        items: cartItems.map(item => ({
          product_id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          // Eski format backward compatibility
          size: item.options?.size,
          strength: item.options?.strength,
          // Yeni dinamik format
          options: item.dynamicOptions || {
            ...(item.options?.size && { boyut: item.options.size }),
            ...(item.options?.strength && { dem: item.options.strength }),
          },
        })),
      };

      // Sipariş oluştur
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Sipariş oluşturulamadı');
      }

      const createdOrder = await response.json();

      // Sipariş başarılı olduktan sonra fiş kullanımını gerçekleştir
      const ticketResult = await ticketApi.useTickets(
        userInfo.id,
        totalCost,
        createdOrder.id, // order_id - sipariş oluşturulduktan sonra ekle
        `Sipariş #${createdOrder.id} için ${totalCost} fiş kullanıldı`
      );

      // Kullanıcının bakiyesini güncelle
      setUserInfo(prev => (prev ? { ...prev, ticket_count: ticketResult.new_balance } : prev));

      // LocalStorage'ı güncelle
      const updatedUser = { ...userInfo, ticket_count: ticketResult.new_balance };
      localStorage.setItem('userInfo', JSON.stringify(updatedUser));

      // Sepeti temizle
      clearCart();

      showSuccess(
        'Sipariş Tamamlandı',
        `Siparişiniz başarıyla oluşturuldu! Sipariş No: #${createdOrder.id}`
      );
    } catch (error) {
      console.error('Sipariş tamamlama hatası:', error);
      showError(
        'Sipariş Hatası',
        error instanceof Error ? error.message : 'Bilinmeyen bir hata oluştu'
      );
      throw error;
    }
  };

  const handleDropdownActions = {
    onBuyTicket: () => {
      setBuyModalOpen(true);
      setDropdownOpen(false);
    },
    onViewOrders: () => {
      navigate('/siparislerim');
      setDropdownOpen(false);
    },
    onProfileSettings: () => {
      setProfileModalOpen(true);
      setDropdownOpen(false);
    },
    onLogout: handleLogout,
  };

  return (
    <header className="bg-white/90 shadow-md sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-5">
        <div className="flex justify-between items-center">
          <Logo />
          <div className="flex items-center gap-5">
            {isAuthenticated && userInfo ? (
              <>
                {userInfo.authorization_level === 'Admin' && (
                  <button
                    onClick={() => navigate('/admin')}
                    className="flex items-center gap-2 bg-gray-800 text-white px-4 py-2 rounded-xl hover:bg-gray-700 transition"
                  >
                    <FaCog />
                    <span>Admin Paneli</span>
                  </button>
                )}
                {typeof userInfo.ticket_count === 'number' && (
                  <span className="bg-green-50 border border-green-200 text-green-700 text-base font-semibold px-4 py-2 rounded-xl shadow-sm mr-1 select-none">
                    Bakiyeniz: {userInfo.ticket_count} fiş
                  </span>
                )}
                <button
                  className="flex items-center space-x-2 bg-gradient-to-r from-green-500 to-green-400 text-white px-5 py-2 rounded-xl font-bold text-base shadow hover:from-green-600 hover:to-green-500 transition-all relative"
                  onClick={() => setCartModalOpen(true)}
                >
                  <FaShoppingCart />
                  <span>Sepetim</span>
                  {cartItems.length > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {cartItems.length}
                    </span>
                  )}
                </button>
                <UserDropdown
                  userInfo={userInfo}
                  dropdownOpen={dropdownOpen}
                  setDropdownOpen={setDropdownOpen}
                  dropdownRef={dropdownRef}
                  onBuyTicket={handleDropdownActions.onBuyTicket}
                  onViewOrders={handleDropdownActions.onViewOrders}
                  onProfileSettings={handleDropdownActions.onProfileSettings}
                  onLogout={handleDropdownActions.onLogout}
                />
              </>
            ) : (
              <button
                onClick={() => setLoginModalOpen(true)}
                className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-6 py-2 rounded-xl font-bold text-base shadow hover:from-blue-700 hover:to-blue-500 transition-all"
              >
                Giriş Yap
              </button>
            )}
          </div>
        </div>
      </div>
      <BuyTicketModal
        isOpen={isBuyModalOpen}
        onClose={() => setBuyModalOpen(false)}
        onBuy={handleBuyTicket}
        userId={userInfo?.id}
      />
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setLoginModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />
      <CartModal
        isOpen={isCartModalOpen}
        onClose={() => setCartModalOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={updateQuantity}
        onRemoveItem={removeFromCart}
        onOrderComplete={handleOrderComplete}
      />
      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        userInfo={userInfo}
        setUserInfo={setUserInfo}
      />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-8px);} to { opacity: 1; transform: translateY(0);} }
        .animate-fadeIn { animation: fadeIn 0.18s cubic-bezier(.4,0,.2,1); }
      `}</style>
    </header>
  );
};

export default Header;
